import { useMutation, useQuery } from '@apollo/client'
import {
  Alert,
  Button,
  ConsoleCard,
  FormFeedback,
  Modal,
  Page,
  PageHeading,
  message,
} from 'admin-web-ui'
import gql from 'graphql-tag'
import React, { useCallback, useState } from 'react'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import { User } from '../../api'
import { useConsoleContext } from '../../containers/Console'
import { ManageAccountUsersList } from './ManageAccountUsersList'

export const GET_ACCOUNT_USERS = gql`
  query {
    users {
      username
      emailAddress
      firstName
      lastName
      enabled
      lastSignIn
      groups
      enabled
      projects {
        id
        name
      }
    }
  }
`

const DISABLE_ACCOUNT_USER = gql`
  mutation disableUser($username: ID!) {
    disableUser(username: $username)
  }
`

const StyledConsoleCard = styled(ConsoleCard)`
  & .ant-card-head {
    box-shadow: inset 0 -1px 0 0 ${({ theme }) => theme.greys.geyser};
  }
  & .ant-card-body {
    padding: 0;
  }
`

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
`

const StyledButton = styled(Button)`
  min-width: 120px;
`

export const RemoveUserButton = styled(StyledButton)`
  margin-left: 8px;
`

export const ManageAccountUsers = (): React.ReactElement => {
  const { me } = useConsoleContext()
  const [modalOpen, setModalOpen] = useState<boolean>(false)
  const [userToDisable, setUserToDisable] = useState<User>()

  const {
    loading,
    data,
    refetch,
    error: userListError,
  } = useQuery(GET_ACCOUNT_USERS, {
    fetchPolicy: 'cache-and-network',
  })

  const [disableUser, { loading: disableUserLoading }] =
    useMutation(DISABLE_ACCOUNT_USER)

  const handleDisableUser = useCallback(async () => {
    setModalOpen(false)
    try {
      await disableUser({
        variables: { username: (userToDisable || {}).username },
      })
      message.success(
        `User ${userToDisable ? userToDisable.emailAddress : ''} disabled`,
      )
      refetch()
    } catch {
      message.error(
        `Disable ${userToDisable ? userToDisable.emailAddress : ''} failed`,
      )
    } finally {
      setUserToDisable(undefined)
    }
  }, [userToDisable, disableUser, refetch])

  return (
    <Page>
      <PageHeading>Manage Account Users</PageHeading>
      <StyledConsoleCard
        title="Users and Access"
        extra={
          <Button type="primary" shape="round">
            <Link to="./add">Add User</Link>
          </Button>
        }
      >
        <FormFeedback showText={!!userListError}>
          <Alert
            message="Error loading users. Please refresh your browser."
            type="error"
          />
        </FormFeedback>
        <ManageAccountUsersList
          users={(data || {}).users || []}
          onDisable={(user) => {
            setModalOpen(true)
            setUserToDisable(user)
          }}
          loading={loading}
          disableUserLoading={disableUserLoading}
          pendingUsernameToDisable={(userToDisable || {}).username}
          currentUsername={me?.username ?? ''}
        />
      </StyledConsoleCard>
      <Modal open={modalOpen} footer={null} title="Disable this user account?">
        <p>This will permanently disable the account user.</p>
        <ModalFooter>
          <StyledButton
            id="remove-user-confirmation-cancel-button"
            type="default"
            shape="round"
            onClick={() => {
              setModalOpen(false)
              setUserToDisable(undefined)
            }}
          >
            Cancel
          </StyledButton>
          <RemoveUserButton
            id="remove-user-confirmation-delete-button"
            danger={true}
            shape="round"
            loading={disableUserLoading}
            onClick={handleDisableUser}
          >
            Disable User
          </RemoveUserButton>
        </ModalFooter>
      </Modal>
    </Page>
  )
}
